import { err, ok } from 'neverthrow';
import { safeAsync } from '@morph-mapper/utils';
import { PluginFrameworkErrorType } from '@morph-mapper/plugins';
import { useStore } from '@/store';
import { iteratorKeys } from '@/config';
import { renderTemplate } from '../api/templating/crud';
import { extractGraphResult } from '../api/templating/parse-utils';
import { useRenderTemplate } from './use-render-template';

/**
 * Same context as the graph editor's node evaluation: an iterator entry is
 * its own context, any other entry renders inside its parent (and therefore
 * inside the parent's iterator) as a plain item.
 */
export const resolveEntryRenderOptions = (
  entryKey: string,
  entryId: string,
  parentId: string | undefined,
) => {
  if (iteratorKeys.includes(entryKey)) {
    return { contextEntryId: entryId, forceItemRender: false };
  }
  if (parentId === undefined) return undefined;
  return { contextEntryId: parentId, forceItemRender: true };
};

/**
 * Renders a candidate snippet as an entry's value inside its template
 * context, so plugins can check what the entry would really output (one
 * value per iteration) rather than what the snippet yields on its own.
 */
export const useRenderInContext = () => {
  const [variant, textContent] = useStore(({ config: c }) => [
    c.getVariant(),
    c.getTextContent(),
  ]);
  const [getEntryKey, getParentId] = useStore(({ entries: e }) => [
    e.getKey,
    e.getParentId,
  ]);
  const { renderWithContext } = useRenderTemplate();

  return (entryId: string) => async (code: string) => {
    const fail = (message: string) =>
      err({ type: PluginFrameworkErrorType.CORRUPTED_STATE, message });

    const options = resolveEntryRenderOptions(
      getEntryKey(entryId),
      entryId,
      getParentId(entryId),
    );
    if (!options) return fail('No parent: cannot determine render context.');

    let template: unknown;
    try {
      template = renderWithContext(entryId, JSON.parse(code), options);
    } catch (e) {
      return fail(e instanceof Error ? e.message : String(e));
    }
    if (template === undefined) {
      return fail('Could not build the entry template in context.');
    }

    const result = await safeAsync(() =>
      renderTemplate(variant, template, textContent, {}),
    );
    if (result.isErr()) return fail(result.error.message);

    return ok(extractGraphResult(result.value, entryId));
  };
};

/**
 * Renders the current value of a sibling entry (same parent, given key) in
 * its own template context, e.g. the date text next to a date format field.
 */
export const useRenderSibling = () => {
  const renderInContext = useRenderInContext();
  const [getParentId, getChildren, getEntryType] = useStore(({ entries: e }) => [
    e.getParentId,
    e.children,
    e.getType,
  ]);
  const { renderItemEntry } = useRenderTemplate();

  return (entryId: string) => async (key: string) => {
    const fail = (message: string) =>
      err({ type: PluginFrameworkErrorType.PATH_NOT_FOUND, message });

    const parentId = getParentId(entryId);
    if (parentId === undefined) return fail('Entry has no parent.');

    let siblingId: string | undefined;
    try {
      siblingId = getChildren(parentId).getId(key);
    } catch {
      siblingId = undefined;
    }
    if (siblingId === undefined) return fail(`No sibling "${key}".`);

    const template = renderItemEntry(siblingId);
    if (template === undefined) {
      return fail(`Sibling "${key}" has no value (type ${getEntryType(siblingId)}).`);
    }

    return renderInContext(siblingId)(JSON.stringify(template));
  };
};
